/**
 * Wisecure Design System — Ant Design Theme Configuration
 *
 * Maps the Flutter DS tokens to Ant Design's theme system.
 * Use with <ConfigProvider theme={wisecureTheme}>.
 */

import type { ThemeConfig } from 'antd';
import { primary, neutral, error, success, warning, info } from './colors';
import { fontFamily } from './typography';

export const wisecureTheme: ThemeConfig = {
  token: {
    // ── Colors ──
    colorPrimary: primary[400],
    colorPrimaryBg: primary[50],
    colorPrimaryBgHover: primary[100],
    colorPrimaryBorder: primary[200],
    colorPrimaryBorderHover: primary[300],
    colorPrimaryHover: primary[300],
    colorPrimaryActive: primary[500],
    colorPrimaryTextHover: primary[300],
    colorPrimaryText: primary[400],
    colorPrimaryTextActive: primary[500],

    colorSuccess: success[400],
    colorSuccessBg: success[50],
    colorSuccessBorder: success[200],

    colorWarning: warning[400],
    colorWarningBg: warning[50],
    colorWarningBorder: warning[200],

    colorError: error[400],
    colorErrorBg: error[50],
    colorErrorBorder: error[200],

    colorInfo: info[400],
    colorInfoBg: info[50],
    colorInfoBorder: info[200],

    colorLink: primary[400],
    colorLinkHover: primary[300],
    colorLinkActive: primary[500],

    // ── Text ──
    colorText: neutral[900],
    colorTextSecondary: neutral[500],
    colorTextTertiary: neutral[300],
    colorTextQuaternary: neutral[200],
    colorTextDisabled: neutral[200],

    // ── Background ──
    colorBgContainer: neutral[0],
    colorBgElevated: neutral[0],
    colorBgLayout: neutral[50],
    colorBgSpotlight: neutral[900],
    colorBgMask: 'rgba(17, 19, 24, 0.45)',

    // ── Border ──
    colorBorder: neutral[100],
    colorBorderSecondary: neutral[50],

    // ── Typography ──
    fontFamily,
    fontSize: 14,
    fontSizeHeading1: 24,
    fontSizeHeading2: 20,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    fontSizeXL: 20,

    fontWeightStrong: 600,

    // ── Layout ──
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    borderRadiusXS: 4,

    // ── Spacing ──
    marginXS: 8,
    marginSM: 12,
    margin: 16,
    marginMD: 20,
    marginLG: 24,
    marginXL: 32,

    paddingXS: 8,
    paddingSM: 12,
    padding: 16,
    paddingMD: 20,
    paddingLG: 24,
    paddingXL: 32,

    // ── Shadows ──
    boxShadow: '0 1px 2px 0 rgba(0, 53, 127, 0.08)',
    boxShadowSecondary: '0 2px 8px 0 rgba(0, 53, 127, 0.08)',

    // ── Control ──
    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,

    // ── Misc ──
    colorFillQuaternary: primary[50],
    colorFillTertiary: primary[100],
    colorFillSecondary: primary[200],
    colorFill: primary[300],
  },
  components: {
    Button: {
      borderRadius: 8,
      borderRadiusLG: 12,
      fontWeight: 600,
      primaryShadow: '0 1px 2px 0 rgba(0, 53, 127, 0.15)',
    },
    Card: {
      borderRadiusLG: 16,
      paddingLG: 24,
    },
    Input: {
      borderRadius: 8,
      activeBorderColor: primary[400],
      hoverBorderColor: primary[300],
    },
    Select: {
      borderRadius: 8,
      optionSelectedBg: primary[50],
    },
    Table: {
      borderRadius: 12,
      headerBg: neutral[50],
      headerColor: neutral[700],
      rowHoverBg: primary[50],
    },
    Menu: {
      darkItemBg: neutral[900],
      darkSubMenuItemBg: neutral[700],
      darkItemSelectedBg: primary[600],
      darkItemHoverBg: primary[700],
      itemBorderRadius: 8,
      iconSize: 16,
    },
    Layout: {
      headerBg: neutral[0],
      siderBg: neutral[900],
      bodyBg: neutral[50],
    },
    Tag: {
      borderRadiusSM: 100,
    },
    Modal: {
      borderRadiusLG: 16,
    },
    Message: {
      borderRadiusLG: 12,
    },
    Statistic: {
      titleFontSize: 14,
      contentFontSize: 28,
    },
  },
};

/**
 * Color map for status tags, matching the Flutter DS semantic colors.
 * Use with <Tag color={statusColors.validated}>.
 */
export const statusColors = {
  // Document / validation statuses
  PENDING: warning[400],
  VALIDATED: success[400],
  DENIED: error[400],
  NOT_UPLOADED: neutral[300],
  WAITING_ON_INFORMATION: warning[300],
  REJECTED: error[400],

  // Society types
  SECURITY: success[400],
  INSTALLER: info[400],
  DISTRIBUTOR: warning[400],

  // Agent statuses
  AWAITING_REGISTRATION: warning[300],
  DOCUMENTS_PENDING: warning[400],
  DOCUMENTS_VALIDATED: success[400],
  OPERATIONAL: success[500],
  SUSPENDED: error[400],

  // General statuses
  active: success[400],
  inactive: neutral[300],
  suspended: error[400],
} as const;
